import { useState, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

const BookingSection = () => {
  const [form, setForm] = useState({ name: "", email: "", restaurant: "", website: "" });
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "center center"],
  });

  const opacity = useTransform(scrollYProgress, [0, 0.6], [0, 1]);
  const y = useTransform(scrollYProgress, [0, 0.6], [40, 0]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const { data, error } = await supabase.functions.invoke("manage-bookings", {
      body: {
        action: "submit",
        name: form.name,
        email: form.email,
        restaurant: form.restaurant,
        website: form.website, // honeypot
      },
    });
    if (error || data?.error) {
      toast.error("Kunde inte skicka. Kontrollera fälten och försök igen.");
      return;
    }
    toast.success("Tack — vi hör av oss inom kort.");
    setForm({ name: "", email: "", restaurant: "", website: "" });
  };

  return (
    <section
      id="book"
      ref={ref}
      className="bg-white py-[100px] md:py-[120px] px-6"
    >
      <motion.div
        style={{ opacity, y }}
        className="max-w-[480px] mx-auto w-full"
      >
        <div className="text-center mb-10">
          <h2 className="text-[clamp(1.75rem,3.5vw,2.375rem)] font-bold tracking-tight text-foreground leading-[1.18] text-balance">
            Boka ett möte
          </h2>
          <p
            className="mt-4 text-[15px]"
            style={{ color: "rgba(15,23,42,0.7)" }}
          >
            Se hur KitchenFlow fungerar för din restaurang.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="soft-card p-6 md:p-8 space-y-3"
        >
          {/* Honeypot — hidden from real users; bots typically fill all fields */}
          <input
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            value={form.website}
            onChange={(e) => setForm({ ...form, website: e.target.value })}
            style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
            aria-hidden="true"
          />
          <Input
            placeholder="Namn"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
            className="h-11 rounded-xl bg-white text-sm border-[rgba(15,23,42,0.1)]"
          />
          <Input
            placeholder="Restaurang"
            value={form.restaurant}
            onChange={(e) => setForm({ ...form, restaurant: e.target.value })}
            required
            className="h-11 rounded-xl bg-white text-sm border-[rgba(15,23,42,0.1)]"
          />
          <Input
            type="email"
            placeholder="E-post"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            required
            className="h-11 rounded-xl bg-white text-sm border-[rgba(15,23,42,0.1)]"
          />
          <Button
            type="submit"
            style={{ backgroundColor: "#026133", borderRadius: "12px" }}
            className="cta-glow w-full h-11 px-6 text-sm font-medium text-white hover:brightness-110 border-0 mt-2"
          >
            Skicka
            <ArrowRight className="ml-2 h-3.5 w-3.5" />
          </Button>
        </form>
      </motion.div>
    </section>
  );
};

export default BookingSection;
